import { AnimatePresence, motion } from 'framer-motion';
import { useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { springGentle, springQuick, tapScale } from '../../tokens';

type Props = {
  title: string;
  /** Shown next to the title in tertiary color: "Income · 3". */
  count?: number;
  /** Open by default? Defaults to true. */
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
};

/**
 * Section header that animates its body open and closed. Sentence-case
 * heading sits flush with the page chrome; tap anywhere on the header
 * to toggle.
 */
export function CollapsibleSection({
  title,
  count,
  defaultOpen = true,
  children,
  className,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={cn('mb-section-gap', className)}>
      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        whileTap={tapScale}
        transition={springQuick}
        aria-expanded={open}
        className="flex items-baseline gap-2 w-full mb-2 px-1 min-h-tap text-left"
      >
        <span className="text-[14px] font-semibold text-text-primary">
          {title}
        </span>
        {typeof count === 'number' && count > 0 ? (
          <span className="text-[13px] font-medium text-text-tertiary tabular-nums">
            {count}
          </span>
        ) : null}
      </motion.button>

      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springGentle}
            style={{ overflow: 'hidden' }}
          >
            {children}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
